import { authorizedApiClient } from './config';

// NOTE: authorizedApiClient.baseURL already includes `/api/v1` (VITE_BASE_URL),
// so every path below is relative to the workspace segment.

export interface ConnectionCreatedPayload {
  connection_id: string;
  primary_email: string;
  external_account_id?: string;
  set_as_default?: boolean;
}

export const connectorsApi = {
  connectionCreated: (workspaceId: string, provider: string, payload: ConnectionCreatedPayload) =>
    authorizedApiClient.post(
      `/${workspaceId}/connectors/${provider}/connection-created`,
      payload,
    ),

  approveDraft: (
    workspaceId: string,
    draftId: string,
    updatedFields: Record<string, unknown>,
    sessionId?: string,
  ) =>
    authorizedApiClient.post(
      `/${workspaceId}/connectors/drafts/${draftId}/approve`,
      { updated_fields: updatedFields },
      sessionId ? { headers: { 'X-Session-Id': sessionId } } : undefined,
    ),

  getDraft: (workspaceId: string, draftId: string) =>
    authorizedApiClient.get(`/${workspaceId}/connectors/drafts/${draftId}`),

  rejectDraft: (
    workspaceId: string,
    draftId: string,
    reason: string | undefined,
    sessionId?: string,
  ) =>
    authorizedApiClient.post(
      `/${workspaceId}/connectors/drafts/${draftId}/reject`,
      { reason },
      sessionId ? { headers: { 'X-Session-Id': sessionId } } : undefined,
    ),

  getPreferences: (workspaceId: string) =>
    authorizedApiClient.get(`/${workspaceId}/connectors/preferences`),

  updatePreferences: (workspaceId: string, approvalRequired: Record<string, boolean>) =>
    authorizedApiClient.patch(`/${workspaceId}/connectors/preferences`, {
      approval_required: approvalRequired,
    }),

  getAvailable: (workspaceId: string) =>
    authorizedApiClient.get(`/${workspaceId}/connectors/available`),

  getMine: (workspaceId: string) =>
    authorizedApiClient.get(`/${workspaceId}/connectors/me`),

  // One-shot reconciliation: fill in options.connection_id for any active
  // member_integration rows that don't have it, using Membrane's
  // /connections list for the current user. Idempotent.
  backfillConnectionIds: (workspaceId: string) =>
    authorizedApiClient.post(`/${workspaceId}/connectors/backfill-connection-ids`),

  setDefaultAccount: (workspaceId: string, provider: string, primaryEmail: string) =>
    authorizedApiClient.post(
      `/${workspaceId}/connectors/${provider}/set-default`,
      { primary_email: primaryEmail },
    ),

  // Returns { drafts: PendingDraft[], total: number }
  // Treats 404 as empty (backend endpoint may not be deployed yet).
  getPendingDrafts: (workspaceId: string) =>
    authorizedApiClient.get(`/${workspaceId}/connectors/drafts/pending`),
};
